import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  Input,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../common/native";
import { SessionContext } from "../../../contexts";
import {
  BillingAdminApiError,
  TAudioDebugRecordsResponse,
  TAudioNoteAttachmentBreakdown,
  TBillingAdminClinicSummary,
  TAudioNotesUsageMetricsResponse,
  TImageMetadataBacklogMetricsResponse,
  TImagesUploadedUsageMetricsResponse,
  TSideBySideUsageMetricsResponse,
  TTransformMetricsBucket,
  TTransformMetricsSeries,
  TTransformsUsageGranularity,
  TTransformsUsageMetricsByProcedureResponse,
  TTransformsUsageMetricsResponse,
  getBillingAdminAudioDebugRecords,
  getBillingAdminAudioNotesUsageMetrics,
  getBillingAdminImageMetadataBacklogMetrics,
  getBillingAdminImagesUploadedUsageMetrics,
  getBillingAdminSideBySideUsageMetrics,
  getBillingAdminSummary,
  getBillingAdminTransformsUsageMetrics,
  getBillingAdminTransformsUsageMetricsByProcedure,
  TBillingAdminAuth,
} from "../../../services/api-billing-admin";
import { ADMIN_TOKENS } from "../../../styles/adminTokens";
import AdminAnalyticsTabs from "./AdminAnalyticsTabs";

type MetricKey = "simulations" | "transform_images";
type ClinicSortKey = "gross_simulations" | "gross_transform_images";
type AnalyticsSource = "clinics" | "procedureGroups";
type AudioTrendMetric =
  | "audio_total"
  | "audio_uploaded"
  | "audio_not_uploaded"
  | "notes_total";
type ImagesTrendMetric = "total" | "by_type";
type SideBySideTrendMetric =
  | "images_total"
  | "images_uploaded"
  | "images_not_uploaded";
type AnalyticsScopeMode = "allClinics" | "clinic" | "globalOnly";
type MetricsResponse =
  | TTransformsUsageMetricsResponse
  | TTransformsUsageMetricsByProcedureResponse;
type BreakdownRow = {
  key: string;
  primaryLabel: string;
  secondaryLabel: string;
  gross_simulations: number;
  gross_transform_images: number;
  series: TTransformMetricsSeries;
};

type AudioNotesClinicRow = {
  key: string;
  clinicName: string;
  clinicUuid: string;
  totalAudio: number;
  uploadedAudio: number;
  notUploadedAudio: number;
  uploadErrorCount: number;
  dominantUploadMode: string;
  attachmentSummary: string;
  notesByType: Record<string, number>;
};

type ImagesClinicRow = {
  key: string;
  clinicName: string;
  clinicUuid: string;
  totalImages: number;
  imagesByType: Record<string, number>;
};

type SideBySideClinicRow = {
  key: string;
  clinicName: string;
  clinicUuid: string;
  total: number;
  uploaded: number;
  notUploaded: number;
};

const TOKENS = ADMIN_TOKENS;

const GRANULARITY_OPTIONS: {
  key: TTransformsUsageGranularity;
  label: string;
}[] = [
  { key: "daily", label: "Day" },
  { key: "weekly", label: "Week" },
  { key: "monthly", label: "Month" },
  { key: "yearly", label: "Year" },
];

const DATE_PRESET_OPTIONS = [
  "Month to date (MTD)",
  "This month",
  "Last month",
  "Last 30 days",
  "Last 60 days",
  "Last 90 days",
  "Last 6 months",
  "Last 12 months",
];

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPresetDateRange = (preset: string) => {
  const today = new Date();
  const endDate = formatDateInput(today);

  switch (preset) {
    case "Month to date (MTD)":
    case "This month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        startDate: formatDateInput(start),
        endDate: formatDateInput(end),
      };
    }
    case "Last 30 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 60 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 59);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 90 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 6 months": {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 12 months": {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 12);
      return { startDate: formatDateInput(start), endDate };
    }
    default:
      return { startDate: "", endDate: "" };
  }
};

const parseDateOnly = (dateString?: string) => {
  if (!dateString) return null;
  const [year, month, day] = dateString
    .split("-")
    .map((value) => Number(value));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDateOnly = (dateString?: string) => {
  const date = parseDateOnly(dateString);
  if (!date) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return { local: "—", utc: "—" };
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return { local: dateString, utc: "—" };
  return {
    local: date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
    utc: date.toUTCString(),
  };
};

const formatEpochSeconds = (value?: number | null) => {
  if (value === null || value === undefined) return { local: "—", utc: "—" };
  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) return { local: String(value), utc: "—" };
  return {
    local: date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
    utc: date.toUTCString(),
  };
};

const formatNumber = (value?: number | null) => {
  if (value === null || value === undefined) return "0";
  return new Intl.NumberFormat("en-US").format(value);
};

const formatAttachmentBreakdown = (
  breakdown?: TAudioNoteAttachmentBreakdown,
) => {
  if (!breakdown) return "—";
  return `0:${formatNumber(breakdown.notes_0)} 1:${formatNumber(
    breakdown.notes_1,
  )} 2:${formatNumber(breakdown.notes_2)} 3+:${formatNumber(
    breakdown.notes_3_plus,
  )}`;
};

const formatBucketLabel = (
  periodStart: string,
  granularity: TTransformsUsageGranularity,
) => {
  const date = parseDateOnly(periodStart);
  if (!date) return periodStart;

  if (granularity === "yearly") {
    return String(date.getFullYear());
  }
  if (granularity === "monthly") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "2-digit",
    });
  }
  if (granularity === "weekly") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
};

const formatProcedureToken = (value?: string) => {
  const token = value?.trim();
  if (!token) return "Unknown";
  if (token.toLowerCase() === "unknown") return "Unknown";

  const normalized = token.replace(/[_-]+/g, " ");
  return normalized
    .split(" ")
    .filter(Boolean)
    .map((part) => {
      if (/^[a-z]{2,4}$/i.test(part)) {
        return part.toUpperCase();
      }
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(" ");
};

const formatCategoryLabel = (value?: string) => {
  const token = value?.trim();
  if (!token) return "Unknown";
  if (token.toLowerCase() === "unknown") return "Unknown";

  return token
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatProcedureGroupLabel = (procedureTypes: string[]) => {
  if (!procedureTypes?.length) return "Unknown";
  return procedureTypes.map((item) => formatProcedureToken(item)).join(" + ");
};

const isClinicMetricsResponse = (
  value: MetricsResponse | null,
): value is TTransformsUsageMetricsResponse => {
  return Boolean(value && "clinics" in value);
};

const sumRecordValues = (record?: Record<string, number>) => {
  if (!record) return 0;
  return Object.values(record).reduce(
    (total, value) => total + (Number.isFinite(value) ? value : 0),
    0,
  );
};

const sortRecordEntries = (record?: Record<string, number>) => {
  if (!record) return [] as { key: string; value: number }[];
  return Object.entries(record)
    .map(([key, value]) => ({ key, value }))
    .sort((a, b) => b.value - a.value);
};

const renderMiniTrend = (
  buckets: TTransformMetricsBucket[],
  metric: MetricKey,
) => {
  if (!buckets.length) {
    return <Text style={styles.miniTrendEmpty}>No data</Text>;
  }
  const values = buckets.map((bucket) => bucket[metric]);
  const maxValue = Math.max(1, ...values);

  return (
    <View style={styles.miniTrendTrack}>
      {buckets.map((bucket) => {
        const value = bucket[metric];
        return (
          <View
            key={bucket.period_start}
            style={[
              styles.miniTrendBar,
              {
                height: Math.max(
                  TOKENS.size.miniTrendMinBarHeight,
                  (value / maxValue) * TOKENS.size.miniTrendHeight,
                ),
              },
            ]}
          />
        );
      })}
    </View>
  );
};

const WEB_DATE_INPUT_STYLE: React.CSSProperties = {
  height: TOKENS.size.inputHeight,
  borderRadius: TOKENS.radius.md,
  border: `${TOKENS.borderWidth.sm}px solid ${TOKENS.color.borderInput}`,
  padding: `${TOKENS.spacing.sm}px ${TOKENS.spacing.md}px`,
  fontFamily: TOKENS.font.medium,
  fontSize: TOKENS.fontSize.md,
  color: TOKENS.color.textPrimary,
  backgroundColor: TOKENS.color.surface,
};

const DateInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => {
  if (Platform.OS === "web") {
    return (
      <View style={styles.dateField}>
        <Text style={styles.dateFieldLabel}>{label}</Text>
        <input
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={WEB_DATE_INPUT_STYLE}
        />
      </View>
    );
  }

  return (
    <View style={styles.dateField}>
      <Input
        name={label}
        placeholder="YYYY-MM-DD"
        value={value}
        onChangeText={onChange}
      />
    </View>
  );
};

const AnalyticsSectionHeader: React.FC<{
  title: string;
  subtitle: string;
  scopeMode: AnalyticsScopeMode;
  clinicName?: string;
  clinicId?: string | null;
}> = ({ title, subtitle, scopeMode, clinicName, clinicId }) => {
  const isClinicScope = scopeMode === "clinic";
  const scopeLabel = isClinicScope
    ? "Clinic filter applied"
    : scopeMode === "globalOnly"
      ? "Global only"
      : "All clinics";
  const scopeValue = isClinicScope
    ? clinicName || "Selected clinic"
    : scopeMode === "globalOnly" && clinicId
      ? "Clinic filter not applied"
      : "Combined analytics";
  const scopeDetail = isClinicScope
    ? clinicId || "Clinic UUID unavailable"
    : scopeMode === "globalOnly"
      ? "This section always shows all clinics."
      : "No clinic UUID is filtering this section.";

  return (
    <View style={styles.sectionHeaderRow}>
      <View style={styles.sectionHeaderText}>
        <Text style={styles.cardTitle}>{title}</Text>
        <Text style={styles.cardSubtitle}>{subtitle}</Text>
      </View>
      <View
        style={[styles.scopeBadge, isClinicScope && styles.scopeBadgeClinic]}
      >
        <Text
          style={[
            styles.scopeBadgeLabel,
            isClinicScope && styles.scopeBadgeLabelClinic,
          ]}
        >
          {scopeLabel}
        </Text>
        <Text style={styles.scopeBadgeValue}>{scopeValue}</Text>
        <Text style={styles.scopeBadgeDetail}>{scopeDetail}</Text>
      </View>
    </View>
  );
};

const AdminAnalyticsPage: React.FC = () => {
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;

  const [activeAuthMode, setActiveAuthMode] = useState<"apiKey" | "jwt">(
    "apiKey",
  );
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [authSecret, setAuthSecret] = useState<TBillingAdminAuth | null>(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [datePreset, setDatePreset] = useState("");
  const [isQuickRangeModalVisible, setIsQuickRangeModalVisible] =
    useState(false);
  const [clinicSearch, setClinicSearch] = useState("");
  const [clinicIdInput, setClinicIdInput] = useState("");
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null);
  const [analyticsSource, setAnalyticsSource] =
    useState<AnalyticsSource>("clinics");
  const [granularity, setGranularity] =
    useState<TTransformsUsageGranularity>("daily");
  const [chartMetric, setChartMetric] = useState<MetricKey>("simulations");
  const [clinicSortMetric, setClinicSortMetric] =
    useState<ClinicSortKey>("gross_simulations");
  const [audioTrendMetric, setAudioTrendMetric] =
    useState<AudioTrendMetric>("audio_total");
  const [imagesTrendMetric, setImagesTrendMetric] =
    useState<ImagesTrendMetric>("total");
  const [sideBySideTrendMetric, setSideBySideTrendMetric] =
    useState<SideBySideTrendMetric>("images_total");
  const [audioDebugFailuresOnly, setAudioDebugFailuresOnly] = useState(true);
  const [audioDebugLimit, setAudioDebugLimit] = useState("100");

  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  const [audioNotesMetrics, setAudioNotesMetrics] =
    useState<TAudioNotesUsageMetricsResponse | null>(null);
  const [audioNotesError, setAudioNotesError] = useState<string | null>(null);
  const [isLoadingAudioNotes, setIsLoadingAudioNotes] = useState(false);

  const [imagesMetrics, setImagesMetrics] =
    useState<TImagesUploadedUsageMetricsResponse | null>(null);
  const [imagesError, setImagesError] = useState<string | null>(null);
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  const [sideBySideMetrics, setSideBySideMetrics] =
    useState<TSideBySideUsageMetricsResponse | null>(null);
  const [sideBySideError, setSideBySideError] = useState<string | null>(null);
  const [isLoadingSideBySide, setIsLoadingSideBySide] = useState(false);

  const [metadataBacklog, setMetadataBacklog] =
    useState<TImageMetadataBacklogMetricsResponse | null>(null);
  const [metadataBacklogError, setMetadataBacklogError] = useState<
    string | null
  >(null);
  const [isLoadingMetadataBacklog, setIsLoadingMetadataBacklog] =
    useState(false);

  const [audioDebugRecords, setAudioDebugRecords] =
    useState<TAudioDebugRecordsResponse | null>(null);
  const [audioDebugError, setAudioDebugError] = useState<string | null>(null);
  const [isLoadingAudioDebug, setIsLoadingAudioDebug] = useState(false);

  const [clinicOptions, setClinicOptions] = useState<
    TBillingAdminClinicSummary[]
  >([]);
  const [clinicOptionsError, setClinicOptionsError] = useState<string | null>(
    null,
  );
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);

  const transformRequestIdRef = useRef(0);
  const audioNotesRequestIdRef = useRef(0);
  const imagesRequestIdRef = useRef(0);
  const sideBySideRequestIdRef = useRef(0);
  const metadataBacklogRequestIdRef = useRef(0);
  const audioDebugRequestIdRef = useRef(0);
  const clinicsRequestIdRef = useRef(0);

  const handleAuthFailure = (error: BillingAdminApiError) => {
    if (error.status === 401 || error.status === 403) {
      setAuthSecret(null);
    }
  };

  const loadTransformMetrics = useCallback(
    async (auth: TBillingAdminAuth, start?: string, end?: string) => {
      transformRequestIdRef.current += 1;
      const requestId = transformRequestIdRef.current;
      setIsLoadingMetrics(true);
      setMetricsError(null);

      try {
        const response =
          analyticsSource === "clinics"
            ? await getBillingAdminTransformsUsageMetrics(auth, {
                startDate: start,
                endDate: end,
              })
            : await getBillingAdminTransformsUsageMetricsByProcedure(auth, {
                startDate: start,
                endDate: end,
              });

        if (requestId !== transformRequestIdRef.current) return;
        setMetrics(response);
      } catch (error) {
        if (requestId !== transformRequestIdRef.current) return;
        if (error instanceof BillingAdminApiError) {
          handleAuthFailure(error);
          setMetricsError(error.message);
        } else if (error instanceof Error) {
          setMetricsError(error.message);
        } else {
          setMetricsError("Failed to load transform usage analytics.");
        }
      } finally {
        if (requestId === transformRequestIdRef.current) {
          setIsLoadingMetrics(false);
        }
      }
    },
    [analyticsSource],
  );

  const loadAudioNotesMetrics = useCallback(
    async (auth: TBillingAdminAuth, start?: string, end?: string) => {
      audioNotesRequestIdRef.current += 1;
      const requestId = audioNotesRequestIdRef.current;
      setIsLoadingAudioNotes(true);
      setAudioNotesError(null);

      try {
        const response = await getBillingAdminAudioNotesUsageMetrics(auth, {
          startDate: start,
          endDate: end,
        });

        if (requestId !== audioNotesRequestIdRef.current) return;
        setAudioNotesMetrics(response);
      } catch (error) {
        if (requestId !== audioNotesRequestIdRef.current) return;
        if (error instanceof BillingAdminApiError) {
          handleAuthFailure(error);
          setAudioNotesError(error.message);
        } else if (error instanceof Error) {
          setAudioNotesError(error.message);
        } else {
          setAudioNotesError("Failed to load audio and notes usage analytics.");
        }
      } finally {
        if (requestId === audioNotesRequestIdRef.current) {
          setIsLoadingAudioNotes(false);
        }
      }
    },
    [],
  );

  const loadImagesMetrics = useCallback(
    async (auth: TBillingAdminAuth, start?: string, end?: string) => {
      imagesRequestIdRef.current += 1;
      const requestId = imagesRequestIdRef.current;
      setIsLoadingImages(true);
      setImagesError(null);

      try {
        const response = await getBillingAdminImagesUploadedUsageMetrics(auth, {
          startDate: start,
          endDate: end,
        });

        if (requestId !== imagesRequestIdRef.current) return;
        setImagesMetrics(response);
      } catch (error) {
        if (requestId !== imagesRequestIdRef.current) return;
        if (error instanceof BillingAdminApiError) {
          handleAuthFailure(error);
          setImagesError(error.message);
        } else if (error instanceof Error) {
          setImagesError(error.message);
        } else {
          setImagesError("Failed to load images uploaded analytics.");
        }
      } finally {
        if (requestId === imagesRequestIdRef.current) {
          setIsLoadingImages(false);
        }
      }
    },
    [],
  );

  const loadSideBySideMetrics = useCallback(
    async (
      auth: TBillingAdminAuth,
      start?: string,
      end?: string,
      clinicId?: string | null,
    ) => {
      sideBySideRequestIdRef.current += 1;
      const requestId = sideBySideRequestIdRef.current;
      setIsLoadingSideBySide(true);
      setSideBySideError(null);

      try {
        const response = await getBillingAdminSideBySideUsageMetrics(auth, {
          startDate: start,
          endDate: end,
          clinicId: clinicId || undefined,
        });

        if (requestId !== sideBySideRequestIdRef.current) return;
        setSideBySideMetrics(response);
      } catch (error) {
        if (requestId !== sideBySideRequestIdRef.current) return;
        if (error instanceof BillingAdminApiError) {
          handleAuthFailure(error);
          setSideBySideError(error.message);
        } else if (error instanceof Error) {
          setSideBySideError(error.message);
        } else {
          setSideBySideError("Failed to load side-by-side usage metrics.");
        }
      } finally {
        if (requestId === sideBySideRequestIdRef.current) {
          setIsLoadingSideBySide(false);
        }
      }
    },
    [],
  );

  const loadMetadataBacklog = useCallback(
    async (
      auth: TBillingAdminAuth,
      start?: string,
      end?: string,
      clinicId?: string | null,
    ) => {
      metadataBacklogRequestIdRef.current += 1;
      const requestId = metadataBacklogRequestIdRef.current;
      setIsLoadingMetadataBacklog(true);
      setMetadataBacklogError(null);

      try {
        const response = await getBillingAdminImageMetadataBacklogMetrics(
          auth,
          {
            startDate: start,
            endDate: end,
            clinicId: clinicId || undefined,
            limit: 200,
          },
        );

        if (requestId !== metadataBacklogRequestIdRef.current) return;
        setMetadataBacklog(response);
      } catch (error) {
        if (requestId !== metadataBacklogRequestIdRef.current) return;
        if (error instanceof BillingAdminApiError) {
          handleAuthFailure(error);
          setMetadataBacklogError(error.message);
        } else if (error instanceof Error) {
          setMetadataBacklogError(error.message);
        } else {
          setMetadataBacklogError(
            "Failed to load image metadata backlog metrics.",
          );
        }
      } finally {
        if (requestId === metadataBacklogRequestIdRef.current) {
          setIsLoadingMetadataBacklog(false);
        }
      }
    },
    [],
  );

  const loadAudioDebug = useCallback(
    async (
      auth: TBillingAdminAuth,
      start?: string,
      end?: string,
      clinicId?: string | null,
      returnFailuresOnly = true,
      limitOverride?: number,
    ) => {
      audioDebugRequestIdRef.current += 1;
      const requestId = audioDebugRequestIdRef.current;
      setIsLoadingAudioDebug(true);
      setAudioDebugError(null);

      try {
        const response = await getBillingAdminAudioDebugRecords(auth, {
          startDate: start,
          endDate: end,
          clinicId: clinicId || undefined,
          returnFailuresOnly,
          limit: limitOverride,
        });

        if (requestId !== audioDebugRequestIdRef.current) return;
        setAudioDebugRecords(response);
      } catch (error) {
        if (requestId !== audioDebugRequestIdRef.current) return;
        if (error instanceof BillingAdminApiError) {
          handleAuthFailure(error);
          setAudioDebugError(error.message);
        } else if (error instanceof Error) {
          setAudioDebugError(error.message);
        } else {
          setAudioDebugError("Failed to load audio debug records.");
        }
      } finally {
        if (requestId === audioDebugRequestIdRef.current) {
          setIsLoadingAudioDebug(false);
        }
      }
    },
    [],
  );

  const loadClinics = useCallback(async (auth: TBillingAdminAuth) => {
    clinicsRequestIdRef.current += 1;
    const requestId = clinicsRequestIdRef.current;
    setIsLoadingClinics(true);
    setClinicOptionsError(null);

    try {
      const response = await getBillingAdminSummary(auth);
      if (requestId !== clinicsRequestIdRef.current) return;
      setClinicOptions(response.clinics || []);
    } catch (error) {
      if (requestId !== clinicsRequestIdRef.current) return;
      setClinicOptions([]);
      if (error instanceof BillingAdminApiError) {
        handleAuthFailure(error);
        setClinicOptionsError(error.message);
      } else if (error instanceof Error) {
        setClinicOptionsError(error.message);
      } else {
        setClinicOptionsError("Failed to load clinic list.");
      }
    } finally {
      if (requestId === clinicsRequestIdRef.current) {
        setIsLoadingClinics(false);
      }
    }
  }, []);

  const loadAllMetrics = useCallback(
    (
      auth: TBillingAdminAuth,
      start?: string,
      end?: string,
      clinicId?: string | null,
    ) => {
      const parsedAudioDebugLimit = Number(audioDebugLimit);
      loadTransformMetrics(auth, start, end);
      loadAudioNotesMetrics(auth, start, end);
      loadImagesMetrics(auth, start, end);
      loadSideBySideMetrics(auth, start, end, clinicId);
      loadMetadataBacklog(auth, start, end, clinicId);
      loadAudioDebug(
        auth,
        start,
        end,
        clinicId,
        audioDebugFailuresOnly,
        Number.isFinite(parsedAudioDebugLimit) ? parsedAudioDebugLimit : 100,
      );
    },
    [
      audioDebugFailuresOnly,
      audioDebugLimit,
      loadAudioDebug,
      loadAudioNotesMetrics,
      loadImagesMetrics,
      loadMetadataBacklog,
      loadSideBySideMetrics,
      loadTransformMetrics,
    ],
  );

  React.useEffect(() => {
    if (!authSecret) return;

    if (startDate && endDate && startDate > endDate) {
      setMetrics(null);
      setAudioNotesMetrics(null);
      setImagesMetrics(null);
      setSideBySideMetrics(null);
      setMetadataBacklog(null);
      setAudioDebugRecords(null);
      setMetricsError("Start date must be earlier than or equal to end date.");
      setAudioNotesError(
        "Start date must be earlier than or equal to end date.",
      );
      setImagesError("Start date must be earlier than or equal to end date.");
      setSideBySideError(
        "Start date must be earlier than or equal to end date.",
      );
      setMetadataBacklogError(
        "Start date must be earlier than or equal to end date.",
      );
      setAudioDebugError(
        "Start date must be earlier than or equal to end date.",
      );
      return;
    }

    loadAllMetrics(
      authSecret,
      startDate || undefined,
      endDate || undefined,
      selectedClinicId,
    );
  }, [authSecret, startDate, endDate, selectedClinicId, loadAllMetrics]);

  const handleConnectWithApiKey = () => {
    const trimmed = apiKeyInput.trim();
    if (!trimmed) {
      setMetricsError("Enter your billing admin key to connect.");
      return;
    }
    setMetrics(null);
    setMetricsError(null);
    setAudioNotesMetrics(null);
    setAudioNotesError(null);
    setImagesMetrics(null);
    setImagesError(null);
    setSideBySideMetrics(null);
    setSideBySideError(null);
    setMetadataBacklog(null);
    setMetadataBacklogError(null);
    setAudioDebugRecords(null);
    setAudioDebugError(null);
    setAuthSecret({ type: "apiKey", secret: trimmed });
  };

  const handleConnectWithJwt = () => {
    const token = session?.accessToken;
    if (!token) {
      setMetricsError("Sign in with an admin session to use JWT auth.");
      return;
    }
    setMetrics(null);
    setMetricsError(null);
    setAudioNotesMetrics(null);
    setAudioNotesError(null);
    setImagesMetrics(null);
    setImagesError(null);
    setSideBySideMetrics(null);
    setSideBySideError(null);
    setMetadataBacklog(null);
    setMetadataBacklogError(null);
    setAudioDebugRecords(null);
    setAudioDebugError(null);
    setAuthSecret({ type: "jwt", secret: token });
  };

  const handleReset = () => {
    setAuthSecret(null);
    setApiKeyInput("");
    setMetrics(null);
    setMetricsError(null);
    setAudioNotesMetrics(null);
    setAudioNotesError(null);
    setImagesMetrics(null);
    setImagesError(null);
    setSideBySideMetrics(null);
    setSideBySideError(null);
    setMetadataBacklog(null);
    setMetadataBacklogError(null);
    setAudioDebugRecords(null);
    setAudioDebugError(null);
    setIsLoadingMetrics(false);
    setIsLoadingAudioNotes(false);
    setIsLoadingImages(false);
    setIsLoadingSideBySide(false);
    setIsLoadingMetadataBacklog(false);
    setIsLoadingAudioDebug(false);
    setStartDate("");
    setEndDate("");
    setDatePreset("");
    setClinicSearch("");
    setClinicIdInput("");
    setSelectedClinicId(null);
    setAnalyticsSource("clinics");
    setGranularity("daily");
    setChartMetric("simulations");
    setClinicSortMetric("gross_simulations");
    setAudioTrendMetric("audio_total");
    setImagesTrendMetric("total");
    setSideBySideTrendMetric("images_total");
    setAudioDebugFailuresOnly(true);
    setAudioDebugLimit("100");
    setClinicOptions([]);
    setClinicOptionsError(null);
    setIsLoadingClinics(false);
  };

  React.useEffect(() => {
    if (!authSecret) return;
    loadClinics(authSecret);
  }, [authSecret, loadClinics]);

  const clinicSearchResults = useMemo(() => {
    const term = clinicSearch.trim().toLowerCase();
    if (!term) return clinicOptions.slice(0, 8);
    return clinicOptions
      .filter((clinic) => {
        const name = clinic.clinic_name?.toLowerCase() || "";
        const uuid = clinic.clinic_uuid?.toLowerCase() || "";
        return name.includes(term) || uuid.includes(term);
      })
      .slice(0, 8);
  }, [clinicOptions, clinicSearch]);

  const selectedClinicOption = useMemo(
    () =>
      clinicOptions.find((clinic) => clinic.clinic_uuid === selectedClinicId) ||
      null,
    [clinicOptions, selectedClinicId],
  );
  const selectedClinicName =
    selectedClinicOption?.clinic_name || "Selected clinic";
  const clinicScopeMode: AnalyticsScopeMode = selectedClinicId
    ? "clinic"
    : "allClinics";
  const transformScopeMode: AnalyticsScopeMode =
    analyticsSource === "procedureGroups" ? "globalOnly" : clinicScopeMode;
  const hasPendingClinicFilter =
    clinicIdInput.trim() !== (selectedClinicId || "");

  const applyClinicFilter = () => {
    const clinicId = clinicIdInput.trim();
    setClinicIdInput(clinicId);
    setSelectedClinicId(clinicId || null);
  };

  const selectedClinicTransform = useMemo(() => {
    if (!isClinicMetricsResponse(metrics) || !selectedClinicId) return null;
    return (
      metrics.clinics.find(
        (clinic) => clinic.clinic_uuid === selectedClinicId,
      ) || null
    );
  }, [metrics, selectedClinicId]);

  const selectedTransformSummary =
    analyticsSource === "clinics" && selectedClinicId
      ? selectedClinicTransform
      : metrics;

  const selectedSeries: TTransformMetricsBucket[] =
    analyticsSource === "clinics" && selectedClinicId
      ? selectedClinicTransform?.series?.[granularity] || []
      : metrics?.series?.[granularity] || [];
  const chartValues = selectedSeries.map(
    (bucket: TTransformMetricsBucket) => bucket[chartMetric],
  );
  const chartMaxValue = Math.max(
    TOKENS.size.chartMaxBaseline,
    ...chartValues,
    0,
  );

  const sortedRows = useMemo<BreakdownRow[]>(() => {
    if (!metrics) return [];

    if (isClinicMetricsResponse(metrics)) {
      const clinics = selectedClinicId
        ? metrics.clinics.filter(
            (clinic) => clinic.clinic_uuid === selectedClinicId,
          )
        : metrics.clinics;
      return [...clinics]
        .sort((a, b) => b[clinicSortMetric] - a[clinicSortMetric])
        .map((clinic) => ({
          key: clinic.clinic_uuid,
          primaryLabel: clinic.clinic_name || "Unnamed clinic",
          secondaryLabel: clinic.clinic_uuid,
          gross_simulations: clinic.gross_simulations,
          gross_transform_images: clinic.gross_transform_images,
          series: clinic.series,
        }));
    }

    return [...metrics.procedure_groups]
      .sort((a, b) => b[clinicSortMetric] - a[clinicSortMetric])
      .map((group) => ({
        key: group.procedure_group_key,
        primaryLabel: formatProcedureGroupLabel(group.procedure_types),
        secondaryLabel: group.procedure_group_key,
        gross_simulations: group.gross_simulations,
        gross_transform_images: group.gross_transform_images,
        series: group.series,
      }));
  }, [metrics, clinicSortMetric, selectedClinicId]);

  const audioClinicRows = useMemo<AudioNotesClinicRow[]>(() => {
    if (!audioNotesMetrics?.clinics?.length) return [];
    const clinics = selectedClinicId
      ? audioNotesMetrics.clinics.filter(
          (clinic) => clinic.clinic_uuid === selectedClinicId,
        )
      : audioNotesMetrics.clinics;
    return [...clinics]
      .map((clinic) => ({
        key: clinic.clinic_uuid,
        clinicName: clinic.clinic_name || "Unnamed clinic",
        clinicUuid: clinic.clinic_uuid,
        totalAudio: clinic.gross_audio_total,
        uploadedAudio: clinic.gross_audio_uploaded,
        notUploadedAudio: clinic.gross_audio_not_uploaded,
        uploadErrorCount: clinic.gross_audio_with_upload_error ?? 0,
        dominantUploadMode:
          sortRecordEntries(clinic.gross_audio_by_upload_mode)[0]?.key || "—",
        attachmentSummary: formatAttachmentBreakdown(
          clinic.audio_note_attachment_breakdown,
        ),
        notesByType: clinic.gross_notes_by_type,
      }))
      .sort((a, b) => b.totalAudio - a.totalAudio);
  }, [audioNotesMetrics, selectedClinicId]);

  const imageClinicRows = useMemo<ImagesClinicRow[]>(() => {
    if (!imagesMetrics?.clinics?.length) return [];
    const clinics = selectedClinicId
      ? imagesMetrics.clinics.filter(
          (clinic) => clinic.clinic_uuid === selectedClinicId,
        )
      : imagesMetrics.clinics;
    return [...clinics]
      .map((clinic) => ({
        key: clinic.clinic_uuid,
        clinicName: clinic.clinic_name || "Unnamed clinic",
        clinicUuid: clinic.clinic_uuid,
        totalImages: sumRecordValues(clinic.gross_images_by_type),
        imagesByType: clinic.gross_images_by_type,
      }))
      .sort((a, b) => b.totalImages - a.totalImages);
  }, [imagesMetrics, selectedClinicId]);

  const selectedAudioClinic = useMemo(() => {
    if (!audioNotesMetrics || !selectedClinicId) return null;
    return (
      audioNotesMetrics.clinics.find(
        (clinic) => clinic.clinic_uuid === selectedClinicId,
      ) || null
    );
  }, [audioNotesMetrics, selectedClinicId]);

  const selectedImagesClinic = useMemo(() => {
    if (!imagesMetrics || !selectedClinicId) return null;
    return (
      imagesMetrics.clinics.find(
        (clinic) => clinic.clinic_uuid === selectedClinicId,
      ) || null
    );
  }, [imagesMetrics, selectedClinicId]);

  const selectedSideBySideClinic = useMemo(() => {
    if (!sideBySideMetrics || !selectedClinicId) return null;
    return (
      sideBySideMetrics.clinics.find(
        (clinic) => clinic.clinic_uuid === selectedClinicId,
      ) || null
    );
  }, [sideBySideMetrics, selectedClinicId]);

  const sideBySideClinicRows = useMemo<SideBySideClinicRow[]>(() => {
    if (!sideBySideMetrics?.clinics?.length) return [];
    const clinics = selectedClinicId
      ? sideBySideMetrics.clinics.filter(
          (clinic) => clinic.clinic_uuid === selectedClinicId,
        )
      : sideBySideMetrics.clinics;
    return [...clinics]
      .map((clinic) => ({
        key: clinic.clinic_uuid,
        clinicName: clinic.clinic_name || "Unnamed clinic",
        clinicUuid: clinic.clinic_uuid,
        total: clinic.gross_images_total,
        uploaded: clinic.gross_images_uploaded,
        notUploaded: clinic.gross_images_not_uploaded,
      }))
      .sort((a, b) => b.total - a.total);
  }, [sideBySideMetrics, selectedClinicId]);

  const activeAuthBadge = authSecret
    ? authSecret.type === "apiKey"
      ? "Billing admin key"
      : "Admin JWT"
    : "Disconnected";

  const isAnyLoading =
    isLoadingMetrics ||
    isLoadingAudioNotes ||
    isLoadingImages ||
    isLoadingSideBySide ||
    isLoadingMetadataBacklog ||
    isLoadingAudioDebug;

  const audioSeries = selectedClinicId
    ? selectedAudioClinic?.series?.[granularity] || []
    : audioNotesMetrics?.series?.[granularity] || [];
  const audioSeriesValues = audioSeries.map((bucket) => bucket.audio_total);
  const audioSeriesMax = Math.max(
    TOKENS.size.chartMaxBaseline,
    ...audioSeriesValues,
    0,
  );

  const imageSeries = selectedClinicId
    ? selectedImagesClinic?.series?.[granularity] || []
    : imagesMetrics?.series?.[granularity] || [];
  const imageSeriesValues = imageSeries.map((bucket) =>
    sumRecordValues(bucket.images_by_type),
  );
  const imageSeriesMax = Math.max(
    TOKENS.size.chartMaxBaseline,
    ...imageSeriesValues,
    0,
  );

  const sideBySideSeries = selectedClinicId
    ? selectedSideBySideClinic?.series?.[granularity] || []
    : sideBySideMetrics?.series?.[granularity] || [];
  const sideBySideSeriesValues = sideBySideSeries.map((bucket) => {
    if (sideBySideTrendMetric === "images_uploaded")
      return bucket.images_uploaded;
    if (sideBySideTrendMetric === "images_not_uploaded")
      return bucket.images_not_uploaded;
    return bucket.images_total;
  });
  const sideBySideSeriesMax = Math.max(
    TOKENS.size.chartMaxBaseline,
    ...sideBySideSeriesValues,
    0,
  );

  const selectedAudioSummary = selectedClinicId
    ? selectedAudioClinic
    : audioNotesMetrics;
  const selectedImagesSummary = selectedClinicId
    ? selectedImagesClinic
    : imagesMetrics;
  const audioByUploadModeEntries = sortRecordEntries(
    selectedAudioSummary?.gross_audio_by_upload_mode,
  );
  const audioAttachmentBreakdown =
    selectedAudioSummary?.audio_note_attachment_breakdown;
  const audioSeriesMetricValues = audioSeries.map((bucket) => {
    if (audioTrendMetric === "audio_uploaded") return bucket.audio_uploaded;
    if (audioTrendMetric === "audio_not_uploaded")
      return bucket.audio_not_uploaded;
    if (audioTrendMetric === "notes_total")
      return sumRecordValues(bucket.notes_by_type);
    return bucket.audio_total;
  });
  const audioSeriesMetricMax = Math.max(
    TOKENS.size.chartMaxBaseline,
    ...audioSeriesMetricValues,
    0,
  );

  const imageTypeKeys = useMemo(
    () =>
      Array.from(
        new Set(
          imageSeries.flatMap((bucket) =>
            Object.keys(bucket.images_by_type || {}),
          ),
        ),
      ).sort(),
    [imageSeries],
  );

  const backlogItems =
    metadataBacklog?.items || metadataBacklog?.backlog_items || [];

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.headerRow}>
        <View style={styles.headerTextGroup}>
          <TitleText text="Admin Analytics Dashboard" />
          <Text style={styles.subtitle}>
            Transform, audio, notes, and image usage metrics with the active
            data scope shown on every section.
          </Text>
        </View>
        <View style={styles.badgeWrapper}>
          <Text style={styles.authBadgeLabel}>Auth mode</Text>
          <Text style={[styles.authBadge, !authSecret && styles.authBadgeIdle]}>
            {activeAuthBadge}
          </Text>
        </View>
      </View>
      <AdminAnalyticsTabs activeTab="analytics" />

      <View style={[styles.card, styles.narrowCard]}>
        <Text style={styles.cardTitle}>Connection</Text>
        <Text style={styles.cardSubtitle}>
          Authenticate with either a billing admin key or an admin bearer
          session.
        </Text>

        <View style={styles.authToggleRow}>
          <SecondaryButton
            text="Billing Admin Key"
            onPress={() => setActiveAuthMode("apiKey")}
            selected={activeAuthMode === "apiKey"}
            style={styles.authToggleButton}
          />
          <SecondaryButton
            text="Admin JWT"
            onPress={() => setActiveAuthMode("jwt")}
            selected={activeAuthMode === "jwt"}
            style={styles.authToggleButton}
          />
        </View>

        {activeAuthMode === "apiKey" ? (
          <View style={styles.authContent}>
            <Input
              name="Billing Admin Key"
              placeholder="Paste admin key"
              value={apiKeyInput}
              onChangeText={setApiKeyInput}
              autoCapitalize="none"
              secureTextEntry
            />
            <PrimaryButton
              text="Connect with key"
              onPress={handleConnectWithApiKey}
            />
          </View>
        ) : (
          <View style={styles.authContent}>
            <Text style={styles.jwtHint}>
              {session?.accessToken
                ? `Using current session token (${session.userEmail}).`
                : "Sign in to an admin account to use JWT auth."}
            </Text>
            <PrimaryButton
              text="Use current admin session"
              onPress={handleConnectWithJwt}
              disabled={!session?.accessToken}
            />
          </View>
        )}

        <SecondaryButton
          text="Reset credentials and filters"
          onPress={handleReset}
        />
      </View>

      {authSecret && (
        <View style={[styles.card, styles.narrowCard]}>
          <Text style={styles.cardTitle}>Filters</Text>
          <Text style={styles.cardSubtitle}>
            Leave dates blank for the backend default 30-day period.
          </Text>
          <Text style={styles.groupLabel}>Transform grouping</Text>
          <Text style={styles.inlineHintText}>
            This setting changes only the Transform Usage section.
          </Text>
          <View style={styles.buttonRow}>
            <SecondaryButton
              text="By clinic"
              selected={analyticsSource === "clinics"}
              onPress={() => setAnalyticsSource("clinics")}
              style={styles.inlineButton}
            />
            <SecondaryButton
              text="By procedure group"
              selected={analyticsSource === "procedureGroups"}
              onPress={() => setAnalyticsSource("procedureGroups")}
              style={styles.inlineButton}
            />
          </View>
          <View style={styles.filtersRow}>
            <DateInput
              label="Start date"
              value={startDate}
              onChange={(value) => {
                setStartDate(value);
                setDatePreset("");
              }}
            />
            <DateInput
              label="End date"
              value={endDate}
              onChange={(value) => {
                setEndDate(value);
                setDatePreset("");
              }}
            />
            <View style={[styles.datePresetField, styles.quickRangeField]}>
              <View style={styles.dateField}>
                <Text style={styles.dateFieldLabel}>Quick range</Text>
                <Pressable
                  style={styles.quickRangeButton}
                  onPress={() => setIsQuickRangeModalVisible(true)}
                >
                  <Text
                    style={[
                      styles.quickRangeButtonText,
                      !datePreset && styles.quickRangeButtonTextPlaceholder,
                    ]}
                  >
                    {datePreset || "Custom range"}
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
          <View
            testID="admin-analytics-clinic-scope"
            style={styles.clinicFilterCard}
          >
            <Text style={styles.groupLabel}>Clinic scope (optional)</Text>
            <Text style={styles.inlineHintText}>
              Search for a clinic or paste its UUID, then apply it. Each
              analytics section shows its active data scope.
            </Text>
            <View style={styles.filtersRow}>
              <View style={styles.datePresetField}>
                <Input
                  name="Clinic name"
                  placeholder="Search clinic name"
                  value={clinicSearch}
                  onChangeText={setClinicSearch}
                  autoCapitalize="none"
                />
              </View>
              <View style={styles.datePresetField}>
                <Input
                  name="Clinic UUID"
                  placeholder="Paste clinic UUID"
                  value={clinicIdInput}
                  onChangeText={setClinicIdInput}
                  autoCapitalize="none"
                />
              </View>
            </View>
            <View style={styles.buttonRow}>
              <SecondaryButton
                text={
                  selectedClinicId
                    ? "Update clinic filter"
                    : "Apply clinic filter"
                }
                onPress={applyClinicFilter}
                disabled={!hasPendingClinicFilter}
                style={styles.inlineButton}
              />
              {hasPendingClinicFilter && (
                <Text style={styles.pendingFilterText}>
                  UUID changes are not applied yet.
                </Text>
              )}
            </View>
            <View style={styles.searchResults}>
              {clinicOptionsError && (
                <Text style={styles.inlineErrorText}>{clinicOptionsError}</Text>
              )}
              {isLoadingClinics && (
                <Text style={styles.inlineHintText}>Loading clinics…</Text>
              )}
              {!isLoadingClinics && clinicSearchResults.length === 0 && (
                <Text style={styles.inlineHintText}>
                  No clinics match the current search.
                </Text>
              )}
              {clinicSearchResults.map((clinic) => (
                <View key={clinic.clinic_uuid} style={styles.searchResultRow}>
                  <View style={styles.searchResultText}>
                    <Text style={styles.searchResultTitle}>
                      {clinic.clinic_name || "Unnamed clinic"}
                    </Text>
                    <Text style={styles.searchResultSubtitle}>
                      {clinic.clinic_uuid}
                    </Text>
                  </View>
                  <SecondaryButton
                    text="Use"
                    onPress={() => {
                      setClinicIdInput(clinic.clinic_uuid);
                      setSelectedClinicId(clinic.clinic_uuid);
                      setClinicSearch(clinic.clinic_name || "");
                    }}
                    style={styles.searchResultButton}
                  />
                </View>
              ))}
            </View>
            {selectedClinicId && (
              <SecondaryButton
                text="Clear clinic filter"
                onPress={() => {
                  setClinicIdInput("");
                  setSelectedClinicId(null);
                  setClinicSearch("");
                }}
                style={styles.inlineButton}
              />
            )}
            <View
              style={[
                styles.activeScopeCard,
                Boolean(selectedClinicId) && styles.activeScopeCardClinic,
              ]}
            >
              <Text style={styles.activeScopeLabel}>Active data scope</Text>
              <Text style={styles.activeScopeValue}>
                {selectedClinicId ? selectedClinicName : "All clinics"}
              </Text>
              <Text style={styles.activeScopeDetail}>
                {selectedClinicId
                  ? selectedClinicId
                  : "No clinic UUID is currently applied."}
              </Text>
              <Text style={styles.activeScopeHint}>
                {selectedClinicId
                  ? "Clinic-level sections are filtered. Global-only sections are labeled separately."
                  : "All sections currently show combined analytics."}
              </Text>
            </View>
          </View>
          <View style={styles.buttonRow}>
            <SecondaryButton
              text="Clear date range"
              onPress={() => {
                setStartDate("");
                setEndDate("");
              }}
              style={styles.inlineButton}
            />
            <PrimaryButton
              text="Refresh"
              onPress={() =>
                authSecret &&
                loadAllMetrics(
                  authSecret,
                  startDate || undefined,
                  endDate || undefined,
                  selectedClinicId,
                )
              }
              style={styles.inlineButton}
            />
          </View>

          <Text style={styles.groupLabel}>Audio debug options</Text>
          <View style={styles.buttonRow}>
            <SecondaryButton
              text="Failures only"
              selected={audioDebugFailuresOnly}
              onPress={() => setAudioDebugFailuresOnly(true)}
              style={styles.inlineButton}
            />
            <SecondaryButton
              text="All records"
              selected={!audioDebugFailuresOnly}
              onPress={() => setAudioDebugFailuresOnly(false)}
              style={styles.inlineButton}
            />
            <View style={styles.datePresetField}>
              <Input
                name="Audio debug limit"
                placeholder="100"
                value={audioDebugLimit}
                onChangeText={setAudioDebugLimit}
                keyboardType="numeric"
              />
            </View>
          </View>

          <Text style={styles.groupLabel}>Trend granularity</Text>
          <View style={styles.buttonRow}>
            {GRANULARITY_OPTIONS.map((option) => (
              <SecondaryButton
                key={option.key}
                text={option.label}
                selected={granularity === option.key}
                onPress={() => setGranularity(option.key)}
                style={styles.inlineButton}
              />
            ))}
          </View>

          <Text style={styles.groupLabel}>Primary metric</Text>
          <View style={styles.buttonRow}>
            <SecondaryButton
              text="Simulations"
              selected={chartMetric === "simulations"}
              onPress={() => setChartMetric("simulations")}
              style={styles.inlineButton}
            />
            <SecondaryButton
              text="Transform Images"
              selected={chartMetric === "transform_images"}
              onPress={() => setChartMetric("transform_images")}
              style={styles.inlineButton}
            />
          </View>
        </View>
      )}

      <Modal
        transparent
        animationType="fade"
        visible={isQuickRangeModalVisible}
        onRequestClose={() => setIsQuickRangeModalVisible(false)}
      >
        <Pressable
          style={styles.quickRangeModalOverlay}
          onPress={() => setIsQuickRangeModalVisible(false)}
        />
        <View style={styles.quickRangeModalCard}>
          <Text style={styles.quickRangeModalTitle}>Quick range</Text>
          <ScrollView style={styles.quickRangeOptionsList}>
            {DATE_PRESET_OPTIONS.map((preset) => {
              const selected = preset === datePreset;
              return (
                <Pressable
                  key={preset}
                  style={[
                    styles.quickRangeOptionButton,
                    selected && styles.quickRangeOptionButtonSelected,
                  ]}
                  onPress={() => {
                    setDatePreset(preset);
                    const range = getPresetDateRange(preset);
                    setStartDate(range.startDate);
                    setEndDate(range.endDate);
                    setIsQuickRangeModalVisible(false);
                  }}
                >
                  <Text
                    style={[
                      styles.quickRangeOptionText,
                      selected && styles.quickRangeOptionTextSelected,
                    ]}
                  >
                    {preset}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
          <SecondaryButton
            text="Close"
            onPress={() => setIsQuickRangeModalVisible(false)}
          />
        </View>
      </Modal>

      {isAnyLoading && (
        <View style={styles.loaderRow}>
          <ActivityIndicator color={TOKENS.color.accent} />
          <Text style={styles.loaderText}>Loading analytics…</Text>
        </View>
      )}

      {metricsError && !isLoadingMetrics && (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>
            Unable to load transform analytics
          </Text>
          <Text style={styles.errorMessage}>{metricsError}</Text>
        </View>
      )}

      {metrics && !isLoadingMetrics && (
        <>
          <View testID="admin-analytics-transforms" style={styles.card}>
            <AnalyticsSectionHeader
              title="Transform Usage"
              subtitle={
                analyticsSource === "clinics"
                  ? "Simulation and transform-image activity by clinic."
                  : "Simulation and transform-image activity by procedure group."
              }
              scopeMode={transformScopeMode}
              clinicName={selectedClinicName}
              clinicId={selectedClinicId}
            />
          </View>
          <View style={styles.periodCard}>
            <Text style={styles.periodText}>
              Period: {formatDateOnly(metrics.start_date)} to{" "}
              {formatDateOnly(metrics.end_date)}
            </Text>
          </View>

          <View style={styles.kpiRow}>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Gross simulations</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(selectedTransformSummary?.gross_simulations)}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Gross transform images</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(selectedTransformSummary?.gross_transform_images)}
              </Text>
            </View>
          </View>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              Transform trend (
              {GRANULARITY_OPTIONS.find((x) => x.key === granularity)?.label})
            </Text>
            {selectedSeries.length === 0 ? (
              <Text style={styles.emptyText}>
                No data returned for this period. Axes are kept stable for zero
                values.
              </Text>
            ) : (
              <View style={styles.chartRoot}>
                <View style={styles.yAxis}>
                  <Text style={styles.axisLabel}>
                    {formatNumber(chartMaxValue)}
                  </Text>
                  <Text style={styles.axisLabel}>
                    {formatNumber(Math.round(chartMaxValue / 2))}
                  </Text>
                  <Text style={styles.axisLabel}>0</Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.chartArea}>
                    {selectedSeries.map((bucket: TTransformMetricsBucket) => {
                      const simulationsHeight =
                        (bucket.simulations / chartMaxValue) * 100;
                      const transformImagesHeight =
                        (bucket.transform_images / chartMaxValue) * 100;

                      return (
                        <View
                          key={bucket.period_start}
                          style={styles.chartBucket}
                        >
                          <View style={styles.bucketBarGroup}>
                            <View
                              style={[
                                styles.chartBar,
                                styles.simulationsBar,
                                {
                                  height: `${Math.max(
                                    TOKENS.size.chartMinBarHeight,
                                    simulationsHeight,
                                  )}%`,
                                  opacity:
                                    chartMetric === "simulations"
                                      ? TOKENS.opacity.full
                                      : TOKENS.opacity.muted,
                                },
                              ]}
                            />
                            <View
                              style={[
                                styles.chartBar,
                                styles.transformImagesBar,
                                {
                                  height: `${Math.max(
                                    TOKENS.size.chartMinBarHeight,
                                    transformImagesHeight,
                                  )}%`,
                                  opacity:
                                    chartMetric === "transform_images"
                                      ? TOKENS.opacity.full
                                      : TOKENS.opacity.muted,
                                },
                              ]}
                            />
                          </View>
                          <Text style={styles.bucketLabel}>
                            {formatBucketLabel(
                              bucket.period_start,
                              granularity,
                            )}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </ScrollView>
              </View>
            )}
            <View style={styles.chartLegend}>
              <View style={styles.legendItem}>
                <View style={[styles.legendSwatch, styles.simulationsBar]} />
                <Text style={styles.legendText}>Simulations</Text>
              </View>
              <View style={styles.legendItem}>
                <View
                  style={[styles.legendSwatch, styles.transformImagesBar]}
                />
                <Text style={styles.legendText}>Transform images</Text>
              </View>
            </View>
          </View>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              {analyticsSource === "clinics"
                ? "Clinic breakdown"
                : "Procedure group breakdown"}
            </Text>
            <Text style={styles.cardSubtitle}>
              Sorted by{" "}
              {clinicSortMetric === "gross_simulations"
                ? "gross simulations"
                : "gross transform images"}
              .
            </Text>
            {analyticsSource === "procedureGroups" && (
              <Text style={styles.cardSubtitle}>
                Counts are based on successful transform request rows.
              </Text>
            )}
            <View style={styles.buttonRow}>
              <SecondaryButton
                text="Sort: Simulations"
                selected={clinicSortMetric === "gross_simulations"}
                onPress={() => setClinicSortMetric("gross_simulations")}
                style={styles.inlineButton}
              />
              <SecondaryButton
                text="Sort: Transform Images"
                selected={clinicSortMetric === "gross_transform_images"}
                onPress={() => setClinicSortMetric("gross_transform_images")}
                style={styles.inlineButton}
              />
            </View>

            {!sortedRows.length ? (
              <Text style={styles.emptyText}>
                {analyticsSource === "clinics"
                  ? selectedClinicId
                    ? "No transform activity found for the selected clinic in this period."
                    : "No clinic analytics available."
                  : "No procedure-group analytics available."}
              </Text>
            ) : (
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.clinicTable}>
                  <View style={styles.tableHeader}>
                    <Text
                      style={[styles.tableHeaderText, styles.tableClinicColumn]}
                    >
                      {analyticsSource === "clinics"
                        ? "Clinic"
                        : "Procedure group"}
                    </Text>
                    <Text style={styles.tableHeaderText}>
                      {analyticsSource === "clinics" ? "UUID" : "Group key"}
                    </Text>
                    <Text style={styles.tableHeaderText}>Simulations</Text>
                    <Text style={styles.tableHeaderText}>Transform Images</Text>
                    <Text style={styles.tableHeaderText}>Mini trend</Text>
                  </View>
                  {sortedRows.map((row) => (
                    <View key={row.key} style={styles.tableRow}>
                      <Text
                        numberOfLines={1}
                        style={[styles.tableCellText, styles.tableClinicColumn]}
                      >
                        {row.primaryLabel}
                      </Text>
                      <Text numberOfLines={1} style={styles.tableCellText}>
                        {row.secondaryLabel}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {formatNumber(row.gross_simulations)}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {formatNumber(row.gross_transform_images)}
                      </Text>
                      <View style={styles.tableTrendCell}>
                        {renderMiniTrend(
                          row.series[granularity] || [],
                          chartMetric,
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              </ScrollView>
            )}
          </View>
        </>
      )}

      {audioNotesError && !isLoadingAudioNotes && (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Unable to load audio + notes</Text>
          <Text style={styles.errorMessage}>{audioNotesError}</Text>
        </View>
      )}

      {audioNotesMetrics && !isLoadingAudioNotes && (
        <View testID="admin-analytics-audio-notes" style={styles.card}>
          <AnalyticsSectionHeader
            title="Audio + Notes Usage"
            subtitle="Rollups and trends for audio recordings and note creation."
            scopeMode={clinicScopeMode}
            clinicName={selectedClinicName}
            clinicId={selectedClinicId}
          />
          <View style={styles.kpiRow}>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Audio total</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(selectedAudioSummary?.gross_audio_total)}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Audio uploaded</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(selectedAudioSummary?.gross_audio_uploaded)}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Audio not uploaded</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(selectedAudioSummary?.gross_audio_not_uploaded)}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Audio upload errors</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(
                  selectedAudioSummary?.gross_audio_with_upload_error,
                )}
              </Text>
            </View>
          </View>

          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Notes by type</Text>
            {sortRecordEntries(selectedAudioSummary?.gross_notes_by_type)
              .length === 0 ? (
              <Text style={styles.emptyText}>No notes recorded.</Text>
            ) : (
              <View style={styles.metricChipRow}>
                {sortRecordEntries(
                  selectedAudioSummary?.gross_notes_by_type,
                ).map((entry) => (
                  <View key={entry.key} style={styles.metricChip}>
                    <Text style={styles.metricChipLabel}>
                      {formatCategoryLabel(entry.key)}
                    </Text>
                    <Text style={styles.metricChipValue}>
                      {formatNumber(entry.value)}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Audio by upload mode</Text>
            {!audioByUploadModeEntries.length ? (
              <Text style={styles.emptyText}>
                No upload-mode data available.
              </Text>
            ) : (
              <View style={styles.metricChipRow}>
                {audioByUploadModeEntries.map((entry) => (
                  <View key={entry.key} style={styles.metricChip}>
                    <Text style={styles.metricChipLabel}>
                      {formatCategoryLabel(entry.key)}
                    </Text>
                    <Text style={styles.metricChipValue}>
                      {formatNumber(entry.value)}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Note attachment breakdown</Text>
            <Text style={styles.inlineHintText}>
              {formatAttachmentBreakdown(audioAttachmentBreakdown)}
            </Text>
          </View>

          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Trend</Text>
            <View style={styles.buttonRow}>
              <SecondaryButton
                text="Audio total"
                selected={audioTrendMetric === "audio_total"}
                onPress={() => setAudioTrendMetric("audio_total")}
                style={styles.inlineButton}
              />
              <SecondaryButton
                text="Uploaded"
                selected={audioTrendMetric === "audio_uploaded"}
                onPress={() => setAudioTrendMetric("audio_uploaded")}
                style={styles.inlineButton}
              />
              <SecondaryButton
                text="Not uploaded"
                selected={audioTrendMetric === "audio_not_uploaded"}
                onPress={() => setAudioTrendMetric("audio_not_uploaded")}
                style={styles.inlineButton}
              />
              <SecondaryButton
                text="Notes total"
                selected={audioTrendMetric === "notes_total"}
                onPress={() => setAudioTrendMetric("notes_total")}
                style={styles.inlineButton}
              />
            </View>
            {audioSeries.length === 0 ? (
              <Text style={styles.emptyText}>
                No audio data in this period.
              </Text>
            ) : (
              <View style={styles.chartRoot}>
                <View style={styles.yAxis}>
                  <Text style={styles.axisLabel}>
                    {formatNumber(audioSeriesMetricMax)}
                  </Text>
                  <Text style={styles.axisLabel}>
                    {formatNumber(Math.round(audioSeriesMetricMax / 2))}
                  </Text>
                  <Text style={styles.axisLabel}>0</Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.chartArea}>
                    {audioSeries.map((bucket) => {
                      const metricValue =
                        audioTrendMetric === "audio_uploaded"
                          ? bucket.audio_uploaded
                          : audioTrendMetric === "audio_not_uploaded"
                            ? bucket.audio_not_uploaded
                            : audioTrendMetric === "notes_total"
                              ? sumRecordValues(bucket.notes_by_type)
                              : bucket.audio_total;
                      const height =
                        (metricValue / Math.max(audioSeriesMetricMax, 1)) * 100;
                      return (
                        <View
                          key={bucket.period_start}
                          style={styles.chartBucket}
                        >
                          <View style={styles.bucketBarGroupSingle}>
                            <View
                              style={[
                                styles.chartBar,
                                styles.audioBar,
                                {
                                  height: `${Math.max(
                                    TOKENS.size.chartMinBarHeight,
                                    height,
                                  )}%`,
                                },
                              ]}
                            />
                          </View>
                          <Text style={styles.bucketLabel}>
                            {formatBucketLabel(
                              bucket.period_start,
                              granularity,
                            )}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </ScrollView>
              </View>
            )}
          </View>

          {!audioClinicRows.length ? (
            <Text style={styles.emptyText}>
              {selectedClinicId
                ? "No audio or note activity found for the selected clinic in this period."
                : "No clinic rollups available."}
            </Text>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.clinicTable}>
                <View style={styles.tableHeader}>
                  <Text
                    style={[styles.tableHeaderText, styles.tableClinicColumn]}
                  >
                    Clinic
                  </Text>
                  <Text style={styles.tableHeaderText}>UUID</Text>
                  <Text style={styles.tableHeaderText}>Audio total</Text>
                  <Text style={styles.tableHeaderText}>Uploaded</Text>
                  <Text style={styles.tableHeaderText}>Not uploaded</Text>
                  <Text style={styles.tableHeaderText}>Upload errors</Text>
                  <Text style={styles.tableHeaderText}>Dominant mode</Text>
                  <Text style={styles.tableHeaderText}>Attachments</Text>
                </View>
                {audioClinicRows.map((row) => (
                  <View key={row.key} style={styles.tableRow}>
                    <Text
                      numberOfLines={1}
                      style={[styles.tableCellText, styles.tableClinicColumn]}
                    >
                      {row.clinicName}
                    </Text>
                    <Text numberOfLines={1} style={styles.tableCellText}>
                      {row.clinicUuid}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatNumber(row.totalAudio)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatNumber(row.uploadedAudio)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatNumber(row.notUploadedAudio)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatNumber(row.uploadErrorCount)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatCategoryLabel(row.dominantUploadMode)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {row.attachmentSummary}
                    </Text>
                  </View>
                ))}
              </View>
            </ScrollView>
          )}
        </View>
      )}

      {imagesError && !isLoadingImages && (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Unable to load images uploaded</Text>
          <Text style={styles.errorMessage}>{imagesError}</Text>
        </View>
      )}

      {imagesMetrics && !isLoadingImages && (
        <View testID="admin-analytics-images" style={styles.card}>
          <AnalyticsSectionHeader
            title="Images Uploaded Usage"
            subtitle="Uploaded image counts by type (non-transform uploads only)."
            scopeMode={clinicScopeMode}
            clinicName={selectedClinicName}
            clinicId={selectedClinicId}
          />

          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Images by type</Text>
            {sortRecordEntries(selectedImagesSummary?.gross_images_by_type)
              .length === 0 ? (
              <Text style={styles.emptyText}>No images uploaded.</Text>
            ) : (
              <View style={styles.metricChipRow}>
                {sortRecordEntries(
                  selectedImagesSummary?.gross_images_by_type,
                ).map((entry) => (
                  <View key={entry.key} style={styles.metricChip}>
                    <Text style={styles.metricChipLabel}>
                      {formatCategoryLabel(entry.key)}
                    </Text>
                    <Text style={styles.metricChipValue}>
                      {formatNumber(entry.value)}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Trend</Text>
            <View style={styles.buttonRow}>
              <SecondaryButton
                text="Total uploaded"
                selected={imagesTrendMetric === "total"}
                onPress={() => setImagesTrendMetric("total")}
                style={styles.inlineButton}
              />
              <SecondaryButton
                text="By upload type"
                selected={imagesTrendMetric === "by_type"}
                onPress={() => setImagesTrendMetric("by_type")}
                style={styles.inlineButton}
              />
            </View>
            {imageSeries.length === 0 ? (
              <Text style={styles.emptyText}>
                No upload data in this period.
              </Text>
            ) : (
              <View style={styles.chartRoot}>
                <View style={styles.yAxis}>
                  <Text style={styles.axisLabel}>
                    {formatNumber(imageSeriesMax)}
                  </Text>
                  <Text style={styles.axisLabel}>
                    {formatNumber(Math.round(imageSeriesMax / 2))}
                  </Text>
                  <Text style={styles.axisLabel}>0</Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.chartArea}>
                    {imageSeries.map((bucket) => {
                      const total = sumRecordValues(bucket.images_by_type);
                      return (
                        <View
                          key={bucket.period_start}
                          style={styles.chartBucket}
                        >
                          <View style={styles.bucketBarGroupSingle}>
                            {imagesTrendMetric === "total" ? (
                              <View
                                style={[
                                  styles.chartBar,
                                  styles.imagesBar,
                                  {
                                    height: `${Math.max(
                                      TOKENS.size.chartMinBarHeight,
                                      (total / Math.max(imageSeriesMax, 1)) *
                                        100,
                                    )}%`,
                                  },
                                ]}
                              />
                            ) : (
                              <View style={styles.bucketStackedBar}>
                                {imageTypeKeys.map((typeKey, index) => {
                                  const typeValue =
                                    bucket.images_by_type?.[typeKey] || 0;
                                  const share =
                                    total > 0 ? typeValue / total : 0;
                                  return (
                                    <View
                                      key={typeKey}
                                      style={[
                                        styles.bucketStackedSegment,
                                        {
                                          height: `${Math.max(
                                            0,
                                            share * 100,
                                          )}%`,
                                          backgroundColor:
                                            index % 2 === 0
                                              ? TOKENS.color.accent
                                              : TOKENS.color.accentAlt,
                                        },
                                      ]}
                                    />
                                  );
                                })}
                              </View>
                            )}
                          </View>
                          <Text style={styles.bucketLabel}>
                            {formatBucketLabel(
                              bucket.period_start,
                              granularity,
                            )}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </ScrollView>
              </View>
            )}
          </View>

          {!imageClinicRows.length ? (
            <Text style={styles.emptyText}>
              {selectedClinicId
                ? "No uploaded-image activity found for the selected clinic in this period."
                : "No clinic rollups available."}
            </Text>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.clinicTable}>
                <View style={styles.tableHeader}>
                  <Text
                    style={[styles.tableHeaderText, styles.tableClinicColumn]}
                  >
                    Clinic
                  </Text>
                  <Text style={styles.tableHeaderText}>UUID</Text>
                  <Text style={styles.tableHeaderText}>Images total</Text>
                  <Text style={styles.tableHeaderText}>Top type</Text>
                  <Text style={styles.tableHeaderText}>Per-type counts</Text>
                </View>
                {imageClinicRows.map((row) => {
                  const topType = sortRecordEntries(row.imagesByType)[0];
                  return (
                    <View key={row.key} style={styles.tableRow}>
                      <Text
                        numberOfLines={1}
                        style={[styles.tableCellText, styles.tableClinicColumn]}
                      >
                        {row.clinicName}
                      </Text>
                      <Text numberOfLines={1} style={styles.tableCellText}>
                        {row.clinicUuid}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {formatNumber(row.totalImages)}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {topType
                          ? `${formatCategoryLabel(topType.key)} (${formatNumber(
                              topType.value,
                            )})`
                          : "—"}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {sortRecordEntries(row.imagesByType)
                          .map(
                            (entry) =>
                              `${formatCategoryLabel(entry.key)}:${formatNumber(
                                entry.value,
                              )}`,
                          )
                          .join(" | ") || "—"}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          )}
        </View>
      )}

      {sideBySideError && !isLoadingSideBySide && (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>
            Unable to load side-by-side usage
          </Text>
          <Text style={styles.errorMessage}>{sideBySideError}</Text>
        </View>
      )}

      {sideBySideMetrics && !isLoadingSideBySide && (
        <View testID="admin-analytics-side-by-side" style={styles.card}>
          <AnalyticsSectionHeader
            title="Side-by-Side Usage"
            subtitle="Upload health for side-by-side image records."
            scopeMode={clinicScopeMode}
            clinicName={selectedClinicName}
            clinicId={selectedClinicId}
          />
          <View style={styles.kpiRow}>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Images total</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(
                  selectedClinicId
                    ? selectedSideBySideClinic?.gross_images_total
                    : sideBySideMetrics.gross_images_total,
                )}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Uploaded</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(
                  selectedClinicId
                    ? selectedSideBySideClinic?.gross_images_uploaded
                    : sideBySideMetrics.gross_images_uploaded,
                )}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Not uploaded</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(
                  selectedClinicId
                    ? selectedSideBySideClinic?.gross_images_not_uploaded
                    : sideBySideMetrics.gross_images_not_uploaded,
                )}
              </Text>
            </View>
          </View>

          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Trend</Text>
            <View style={styles.buttonRow}>
              <SecondaryButton
                text="Total"
                selected={sideBySideTrendMetric === "images_total"}
                onPress={() => setSideBySideTrendMetric("images_total")}
                style={styles.inlineButton}
              />
              <SecondaryButton
                text="Uploaded"
                selected={sideBySideTrendMetric === "images_uploaded"}
                onPress={() => setSideBySideTrendMetric("images_uploaded")}
                style={styles.inlineButton}
              />
              <SecondaryButton
                text="Not uploaded"
                selected={sideBySideTrendMetric === "images_not_uploaded"}
                onPress={() => setSideBySideTrendMetric("images_not_uploaded")}
                style={styles.inlineButton}
              />
            </View>
            {!sideBySideSeries.length ? (
              <Text style={styles.emptyText}>
                No side-by-side data in this range.
              </Text>
            ) : (
              <View style={styles.chartRoot}>
                <View style={styles.yAxis}>
                  <Text style={styles.axisLabel}>
                    {formatNumber(sideBySideSeriesMax)}
                  </Text>
                  <Text style={styles.axisLabel}>
                    {formatNumber(Math.round(sideBySideSeriesMax / 2))}
                  </Text>
                  <Text style={styles.axisLabel}>0</Text>
                </View>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  <View style={styles.chartArea}>
                    {sideBySideSeries.map((bucket) => {
                      const metricValue =
                        sideBySideTrendMetric === "images_uploaded"
                          ? bucket.images_uploaded
                          : sideBySideTrendMetric === "images_not_uploaded"
                            ? bucket.images_not_uploaded
                            : bucket.images_total;
                      return (
                        <View
                          key={bucket.period_start}
                          style={styles.chartBucket}
                        >
                          <View style={styles.bucketBarGroupSingle}>
                            <View
                              style={[
                                styles.chartBar,
                                styles.imagesBar,
                                {
                                  height: `${Math.max(
                                    TOKENS.size.chartMinBarHeight,
                                    (metricValue /
                                      Math.max(sideBySideSeriesMax, 1)) *
                                      100,
                                  )}%`,
                                },
                              ]}
                            />
                          </View>
                          <Text style={styles.bucketLabel}>
                            {formatBucketLabel(
                              bucket.period_start,
                              granularity,
                            )}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                </ScrollView>
              </View>
            )}
          </View>

          {!sideBySideClinicRows.length ? (
            <Text style={styles.emptyText}>
              {selectedClinicId
                ? "No side-by-side activity found for the selected clinic in this period."
                : "No clinic rollups available."}
            </Text>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.clinicTable}>
                <View style={styles.tableHeader}>
                  <Text
                    style={[styles.tableHeaderText, styles.tableClinicColumn]}
                  >
                    Clinic
                  </Text>
                  <Text style={styles.tableHeaderText}>UUID</Text>
                  <Text style={styles.tableHeaderText}>Total</Text>
                  <Text style={styles.tableHeaderText}>Uploaded</Text>
                  <Text style={styles.tableHeaderText}>Not uploaded</Text>
                </View>
                {sideBySideClinicRows.map((row) => (
                  <View key={row.key} style={styles.tableRow}>
                    <Text
                      numberOfLines={1}
                      style={[styles.tableCellText, styles.tableClinicColumn]}
                    >
                      {row.clinicName}
                    </Text>
                    <Text numberOfLines={1} style={styles.tableCellText}>
                      {row.clinicUuid}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatNumber(row.total)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatNumber(row.uploaded)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      {formatNumber(row.notUploaded)}
                    </Text>
                  </View>
                ))}
              </View>
            </ScrollView>
          )}
        </View>
      )}

      {metadataBacklogError && !isLoadingMetadataBacklog && (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>
            Unable to load image metadata backlog
          </Text>
          <Text style={styles.errorMessage}>{metadataBacklogError}</Text>
        </View>
      )}

      {metadataBacklog && !isLoadingMetadataBacklog && (
        <View testID="admin-analytics-metadata-backlog" style={styles.card}>
          <AnalyticsSectionHeader
            title="Image Metadata Backlog"
            subtitle="Current metadata processing backlog and age distribution."
            scopeMode="globalOnly"
            clinicName={selectedClinicName}
            clinicId={selectedClinicId}
          />
          <View style={styles.kpiRow}>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Total unprocessed</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(metadataBacklog.total_unprocessed)}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Under 2 months</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(metadataBacklog.count_under_2m)}
              </Text>
            </View>
            <View style={styles.kpiCard}>
              <Text style={styles.kpiLabel}>Over 2 months</Text>
              <Text style={styles.kpiValue}>
                {formatNumber(metadataBacklog.count_over_2m)}
              </Text>
            </View>
          </View>
          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>As of</Text>
            <Text style={styles.inlineHintText}>
              {formatDateTime(metadataBacklog.as_of).local}
            </Text>
            <Text style={styles.inlineHintText}>
              Avg age (hours): {formatNumber(metadataBacklog.average_age_hours)}
            </Text>
            <Text style={styles.inlineHintText}>
              P50/P95/P99 (sec): {formatNumber(metadataBacklog.p50_age_seconds)}{" "}
              / {formatNumber(metadataBacklog.p95_age_seconds)} /{" "}
              {formatNumber(metadataBacklog.p99_age_seconds)}
            </Text>
            <Text style={styles.inlineHintText}>
              Max age (sec): {formatNumber(metadataBacklog.max_age_seconds)}
            </Text>
            <Text style={styles.inlineHintText}>
              Oldest/Newest:{" "}
              {formatDateTime(metadataBacklog.oldest_created_at).local} /{" "}
              {formatDateTime(metadataBacklog.newest_created_at).local}
            </Text>
          </View>
          <View style={styles.subsection}>
            <Text style={styles.groupLabel}>Age distribution</Text>
            {!metadataBacklog.age_buckets?.length ? (
              <Text style={styles.emptyText}>No age bucket data.</Text>
            ) : (
              <View style={styles.metricChipRow}>
                {metadataBacklog.age_buckets.map((bucket) => (
                  <View key={bucket.label} style={styles.metricChip}>
                    <Text style={styles.metricChipLabel}>{bucket.label}</Text>
                    <Text style={styles.metricChipValue}>
                      {formatNumber(bucket.count)}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
          {!!metadataBacklog.clinics?.length && (
            <View style={styles.subsection}>
              <Text style={styles.groupLabel}>Per-clinic backlog</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.clinicTable}>
                  <View style={styles.tableHeader}>
                    <Text
                      style={[styles.tableHeaderText, styles.tableClinicColumn]}
                    >
                      Clinic
                    </Text>
                    <Text style={styles.tableHeaderText}>UUID</Text>
                    <Text style={styles.tableHeaderText}>Unprocessed</Text>
                    <Text style={styles.tableHeaderText}>Avg age (h)</Text>
                    <Text style={styles.tableHeaderText}>Oldest</Text>
                    <Text style={styles.tableHeaderText}>Newest</Text>
                    <Text style={styles.tableHeaderText}>Age buckets</Text>
                  </View>
                  {metadataBacklog.clinics.map((clinic) => (
                    <View key={clinic.clinic_uuid} style={styles.tableRow}>
                      <Text
                        numberOfLines={1}
                        style={[styles.tableCellText, styles.tableClinicColumn]}
                      >
                        {clinic.clinic_name || "Unnamed clinic"}
                      </Text>
                      <Text numberOfLines={1} style={styles.tableCellText}>
                        {clinic.clinic_uuid}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {formatNumber(clinic.total_unprocessed)}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {formatNumber(clinic.average_age_hours)}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {formatDateTime(clinic.oldest_created_at).local}
                      </Text>
                      <Text style={styles.tableCellText}>
                        {formatDateTime(clinic.newest_created_at).local}
                      </Text>
                      <View style={styles.sparkBars}>
                        {(clinic.age_buckets || []).map((bucket) => (
                          <View
                            key={bucket.label}
                            style={[
                              styles.sparkBar,
                              {
                                height: Math.max(
                                  TOKENS.size.miniTrendMinBarHeight,
                                  bucket.count > 0
                                    ? Math.min(
                                        28,
                                        Math.log2(bucket.count + 1) * 6,
                                      )
                                    : TOKENS.size.miniTrendMinBarHeight,
                                ),
                              },
                            ]}
                          />
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              </ScrollView>
            </View>
          )}
          {!!backlogItems.length && (
            <View style={styles.subsection}>
              <Text style={styles.groupLabel}>Backlog items</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.clinicTable}>
                  <View style={styles.tableHeader}>
                    <Text style={styles.tableHeaderText}>ID</Text>
                    <Text style={styles.tableHeaderText}>Clinic UUID</Text>
                    <Text style={styles.tableHeaderText}>Created</Text>
                    <Text style={styles.tableHeaderText}>Age (seconds)</Text>
                    <Text style={styles.tableHeaderText}>Age (h/d)</Text>
                  </View>
                  {backlogItems.map((item) => {
                    const hours = item.age_seconds / 3600;
                    const days = item.age_seconds / 86400;
                    return (
                      <View key={item.id} style={styles.tableRow}>
                        <Text style={styles.tableCellText}>{item.id}</Text>
                        <Text style={styles.tableCellText}>
                          {item.clinic_uuid}
                        </Text>
                        <Text style={styles.tableCellText}>
                          {formatDateTime(item.created_at).local}
                        </Text>
                        <Text style={styles.tableCellText}>
                          {formatNumber(item.age_seconds)}
                        </Text>
                        <Text style={styles.tableCellText}>
                          {hours.toFixed(1)}h / {days.toFixed(1)}d
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </ScrollView>
            </View>
          )}
        </View>
      )}

      {audioDebugError && !isLoadingAudioDebug && (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>
            Unable to load audio debug records
          </Text>
          <Text style={styles.errorMessage}>{audioDebugError}</Text>
        </View>
      )}

      {audioDebugRecords && !isLoadingAudioDebug && (
        <View testID="admin-analytics-audio-debug" style={styles.card}>
          <AnalyticsSectionHeader
            title="Audio Debug Records"
            subtitle="Debug-level upload/session/note visibility for audio entries."
            scopeMode={clinicScopeMode}
            clinicName={selectedClinicName}
            clinicId={selectedClinicId}
          />
          {!audioDebugRecords.records?.length ? (
            <Text style={styles.emptyText}>
              No debug records for current filters.
            </Text>
          ) : (
            <View style={styles.subsection}>
              {audioDebugRecords.records.map((record, index) => {
                const audio = record.audio || {};
                const uploadSession = record.upload_session;
                const debug = record.debug;
                return (
                  <View
                    key={`${audio.uuid || audio.id || "audio"}-${index}`}
                    style={styles.debugCard}
                  >
                    <Text style={styles.groupLabel}>
                      Audio {audio.uuid || audio.id || "Unknown"}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Clinic: {audio.clinic_uuid || "—"} | Patient:{" "}
                      {audio.patient_uuid || "—"}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Created: {formatDateTime(audio.created_at).local} |
                      Updated: {formatDateTime(audio.updated_at).local}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Recording date:{" "}
                      {formatDateTime(audio.date_of_recording).local}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Type: {audio.type || "—"} | Encounter:{" "}
                      {audio.encounter_type || "—"} | Duration:{" "}
                      {formatNumber(audio.duration_in_seconds)}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Upload mode/status: {audio.upload_mode || "—"} /{" "}
                      {audio.upload_status || "—"}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Uploaded: {String(Boolean(audio.is_uploaded))} | Deleted:{" "}
                      {String(Boolean(audio.is_deleted))} | Dummy:{" "}
                      {String(Boolean(audio.is_dummy_data))}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Upload error: {audio.upload_error || "—"}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Finalized: {formatDateTime(audio.finalized_at).local}
                    </Text>
                    <Text style={styles.tableCellText}>
                      S3 key: {audio.s3_key || "—"}
                    </Text>
                    <Text style={styles.tableCellText}>
                      Transcription key: {audio.transcription_s3_key || "—"}
                    </Text>
                    {uploadSession && (
                      <View style={styles.debugSubsection}>
                        <Text style={styles.metricChipLabel}>
                          Upload session
                        </Text>
                        <Text style={styles.tableCellText}>
                          ID: {String(uploadSession.id || "—")} | Status:{" "}
                          {uploadSession.status || "—"}
                        </Text>
                        <Text style={styles.tableCellText}>
                          Strategy: {uploadSession.storage_strategy || "—"} |
                          Prefix: {uploadSession.s3_prefix || "—"}
                        </Text>
                        <Text style={styles.tableCellText}>
                          Parts: {formatNumber(uploadSession.last_part_number)}{" "}
                          / {formatNumber(uploadSession.expected_part_count)}
                        </Text>
                        <Text style={styles.tableCellText}>
                          Created:{" "}
                          {formatDateTime(uploadSession.created_at).local} |
                          Updated:{" "}
                          {formatDateTime(uploadSession.updated_at).local}
                        </Text>
                      </View>
                    )}
                    {debug && (
                      <View style={styles.debugSubsection}>
                        <Text style={styles.metricChipLabel}>Debug flags</Text>
                        <Text style={styles.tableCellText}>
                          Note count: {formatNumber(debug.note_count)}
                        </Text>
                        <Text style={styles.tableCellText}>
                          Upload error:{" "}
                          {String(Boolean(debug.has_upload_error))} | Has
                          transcription key:{" "}
                          {String(Boolean(debug.has_transcription_key))} |
                          Chunked: {String(Boolean(debug.is_chunked))}
                        </Text>
                      </View>
                    )}
                    <View style={styles.debugSubsection}>
                      <Text style={styles.metricChipLabel}>Notes</Text>
                      {!record.notes?.length ? (
                        <Text style={styles.tableCellText}>No notes.</Text>
                      ) : (
                        record.notes.map((note) => (
                          <Text key={note.id} style={styles.tableCellText}>
                            {note.id} | {note.note_type || "—"} | deleted:
                            {String(Boolean(note.is_deleted))} | patient:{" "}
                            {note.patient_uuid || "—"} | appointment:{" "}
                            {note.appointment_id || "—"} | preview:{" "}
                            {note.note_preview || "—"}
                          </Text>
                        ))
                      )}
                    </View>
                  </View>
                );
              })}
            </View>
          )}
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: TOKENS.color.background,
    flex: 1,
  },
  pageContent: {
    padding: TOKENS.spacing.xxl,
    gap: TOKENS.spacing.lg,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: TOKENS.spacing.lg,
    flexWrap: "wrap",
  },
  headerTextGroup: {
    gap: TOKENS.spacing.sm,
    flexShrink: 1,
  },
  subtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textSecondary,
    maxWidth: TOKENS.size.contentMaxWidth,
  },
  badgeWrapper: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    minWidth: TOKENS.size.badgeMinWidth,
    gap: TOKENS.spacing.xs,
  },
  authBadgeLabel: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textTertiary,
  },
  authBadge: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.accentDark,
  },
  authBadgeIdle: {
    color: TOKENS.color.idle,
  },
  card: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.xl,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.md,
    overflow: "visible",
  },
  narrowCard: {
    width: "100%",
    maxWidth: 750,
  },
  cardTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xl,
    color: TOKENS.color.textPrimary,
  },
  cardSubtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textMuted,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: TOKENS.spacing.md,
  },
  sectionHeaderText: {
    flex: 1,
    minWidth: TOKENS.size.cardMinWidth,
    gap: TOKENS.spacing.xs,
  },
  scopeBadge: {
    minWidth: TOKENS.size.cardMinWidth,
    maxWidth: TOKENS.size.eventCardMaxWidth,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderStrong,
    backgroundColor: TOKENS.color.surfaceMuted,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    gap: TOKENS.spacing.xs,
  },
  scopeBadgeClinic: {
    borderColor: TOKENS.color.borderAccent,
    backgroundColor: TOKENS.color.surfaceAccent,
  },
  scopeBadgeLabel: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textTertiary,
    textTransform: "uppercase",
  },
  scopeBadgeLabelClinic: {
    color: TOKENS.color.accentDark,
  },
  scopeBadgeValue: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  scopeBadgeDetail: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  authToggleRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    flexWrap: "wrap",
  },
  authToggleButton: {
    minWidth: TOKENS.size.authButtonMinWidth,
  },
  authContent: {
    gap: TOKENS.spacing.md,
  },
  jwtHint: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  filtersRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.md,
    flexWrap: "wrap",
    overflow: "visible",
  },
  dateField: {
    flex: 1,
    minWidth: TOKENS.size.dateFieldMinWidth,
    gap: TOKENS.spacing.xs,
  },
  datePresetField: {
    flex: 1,
    minWidth: TOKENS.size.dateFieldMinWidth,
    overflow: "visible",
  },
  quickRangeField: {
    zIndex: TOKENS.zIndex.above,
  },
  quickRangeButton: {
    minHeight: TOKENS.size.inputHeight,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderInput,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    justifyContent: "center",
    backgroundColor: TOKENS.color.surface,
  },
  quickRangeButtonText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  quickRangeButtonTextPlaceholder: {
    color: TOKENS.color.idle,
  },
  quickRangeModalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: TOKENS.color.textPrimary,
    opacity: 0.2,
  },
  quickRangeModalCard: {
    position: "absolute",
    top: "20%",
    left: TOKENS.spacing.lg,
    right: TOKENS.spacing.lg,
    maxHeight: "60%",
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.sm,
  },
  quickRangeModalTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.lg,
    color: TOKENS.color.textPrimary,
  },
  quickRangeOptionsList: {
    maxHeight: 280,
  },
  quickRangeOptionButton: {
    minHeight: 44,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    justifyContent: "center",
  },
  quickRangeOptionButtonSelected: {
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  quickRangeOptionText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  quickRangeOptionTextSelected: {
    color: TOKENS.color.accentDark,
  },
  dateFieldLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  buttonRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    flexWrap: "wrap",
  },
  inlineButton: {
    minWidth: TOKENS.size.inlineButtonMinWidth,
  },
  groupLabel: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  clinicFilterCard: {
    gap: TOKENS.spacing.sm,
  },
  pendingFilterText: {
    alignSelf: "center",
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textMuted,
  },
  activeScopeCard: {
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderStrong,
    backgroundColor: TOKENS.color.surfaceMuted,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  activeScopeCardClinic: {
    borderColor: TOKENS.color.borderAccent,
    backgroundColor: TOKENS.color.surfaceAccent,
  },
  activeScopeLabel: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.accentDark,
    textTransform: "uppercase",
  },
  activeScopeValue: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  activeScopeDetail: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  activeScopeHint: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textMuted,
  },
  searchResults: {
    gap: TOKENS.spacing.sm,
  },
  searchResultRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: TOKENS.spacing.sm,
    padding: TOKENS.spacing.sm,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  searchResultText: {
    flex: 1,
    gap: TOKENS.spacing.xs,
  },
  searchResultTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  searchResultSubtitle: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  searchResultButton: {
    minWidth: TOKENS.size.searchButtonMinWidth,
  },
  inlineHintText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textMuted,
  },
  inlineErrorText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.errorText,
  },
  loaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.sm,
    paddingHorizontal: TOKENS.spacing.xs,
  },
  loaderText: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.textSecondary,
  },
  errorCard: {
    backgroundColor: TOKENS.color.errorSurface,
    borderColor: TOKENS.color.errorBorder,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  errorTitle: {
    fontFamily: TOKENS.font.bold,
    color: TOKENS.color.errorText,
    fontSize: TOKENS.fontSize.md,
  },
  errorMessage: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.errorTextStrong,
    fontSize: TOKENS.fontSize.sm,
  },
  periodCard: {
    backgroundColor: TOKENS.color.surfaceAccent,
    borderColor: TOKENS.color.borderAccent,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
  },
  periodText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.accentDark,
  },
  kpiRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.md,
    flexWrap: "wrap",
  },
  kpiCard: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    minWidth: TOKENS.size.cardMinWidth,
    gap: TOKENS.spacing.xs,
    flex: 1,
  },
  kpiLabel: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  kpiValue: {
    fontFamily: TOKENS.font.extrabold,
    fontSize: TOKENS.fontSize.xxxl,
    color: TOKENS.color.textInverse,
  },
  emptyText: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.textMuted,
    fontSize: TOKENS.fontSize.sm,
  },
  chartRoot: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    alignItems: "flex-end",
  },
  yAxis: {
    height: TOKENS.size.chartHeight,
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: TOKENS.size.chartYAxisPadding,
  },
  axisLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textMuted,
  },
  chartArea: {
    height: TOKENS.size.chartHeight,
    flexDirection: "row",
    alignItems: "flex-end",
    borderBottomWidth: TOKENS.borderWidth.sm,
    borderBottomColor: TOKENS.color.borderStrong,
    paddingHorizontal: TOKENS.spacing.xs,
  },
  chartBucket: {
    width: TOKENS.size.chartBucketWidth,
    marginRight: TOKENS.spacing.sm,
    alignItems: "center",
    justifyContent: "flex-end",
  },
  bucketBarGroup: {
    height: TOKENS.size.chartInnerHeight,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: TOKENS.spacing.xs,
  },
  bucketBarGroupSingle: {
    height: TOKENS.size.chartInnerHeight,
    alignItems: "flex-end",
    justifyContent: "flex-end",
  },
  bucketStackedBar: {
    width: TOKENS.size.chartBarWidth,
    height: TOKENS.size.chartInnerHeight,
    borderTopLeftRadius: TOKENS.radius.sm,
    borderTopRightRadius: TOKENS.radius.sm,
    overflow: "hidden",
    justifyContent: "flex-end",
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  bucketStackedSegment: {
    width: "100%",
  },
  chartBar: {
    width: TOKENS.size.chartBarWidth,
    borderTopLeftRadius: TOKENS.radius.sm,
    borderTopRightRadius: TOKENS.radius.sm,
    minHeight: TOKENS.size.chartMinBarHeight,
  },
  simulationsBar: {
    backgroundColor: TOKENS.color.accent,
  },
  transformImagesBar: {
    backgroundColor: TOKENS.color.accentAlt,
  },
  audioBar: {
    backgroundColor: TOKENS.color.accent,
  },
  imagesBar: {
    backgroundColor: TOKENS.color.accentAlt,
  },
  bucketLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
    marginTop: TOKENS.spacing.sm,
  },
  chartLegend: {
    flexDirection: "row",
    gap: TOKENS.spacing.lg,
    flexWrap: "wrap",
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.sm,
  },
  legendSwatch: {
    width: TOKENS.spacing.md,
    height: TOKENS.spacing.md,
    borderRadius: TOKENS.radius.sm,
  },
  legendText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  clinicTable: {
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.lg,
    overflow: "hidden",
    minWidth: TOKENS.size.tableMinWidth,
  },
  tableHeader: {
    backgroundColor: TOKENS.color.surfaceMuted,
    flexDirection: "row",
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    gap: TOKENS.spacing.sm,
  },
  tableHeaderText: {
    flex: 1,
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  tableClinicColumn: {
    flex: TOKENS.size.tableClinicFlex,
  },
  tableRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    borderTopWidth: TOKENS.borderWidth.sm,
    borderTopColor: TOKENS.color.borderMuted,
    alignItems: "center",
  },
  tableCellText: {
    flex: 1,
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textPrimary,
  },
  tableTrendCell: {
    flex: 1,
    minWidth: TOKENS.size.tableTrendMinWidth,
    alignItems: "flex-start",
  },
  miniTrendTrack: {
    height: TOKENS.size.miniTrendHeight,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: TOKENS.spacing.xs,
  },
  miniTrendBar: {
    width: TOKENS.size.miniTrendBarWidth,
    borderRadius: TOKENS.radius.sm,
    backgroundColor: TOKENS.color.accent,
  },
  miniTrendEmpty: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.idle,
  },
  subsection: {
    gap: TOKENS.spacing.sm,
  },
  metricChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: TOKENS.spacing.sm,
  },
  metricChip: {
    backgroundColor: TOKENS.color.surfaceMuted,
    borderRadius: TOKENS.radius.md,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    paddingVertical: TOKENS.spacing.sm,
    paddingHorizontal: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  metricChipLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  metricChipValue: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  sparkBars: {
    flex: 1,
    flexDirection: "row",
    gap: TOKENS.spacing.xs,
    alignItems: "flex-end",
    minWidth: TOKENS.size.tableTrendMinWidth,
  },
  sparkBar: {
    width: TOKENS.size.miniTrendBarWidth,
    borderRadius: TOKENS.radius.sm,
    backgroundColor: TOKENS.color.accent,
  },
  debugCard: {
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    backgroundColor: TOKENS.color.surfaceMuted,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  debugSubsection: {
    marginTop: TOKENS.spacing.xs,
    gap: TOKENS.spacing.xs,
  },
});

export default AdminAnalyticsPage;
